import { createTechradarHandler } from '../../../src/techradarHandler'
import { Resolver, storage } from './forge-api-mock'

const handler = createTechradarHandler({ storage, Resolver })
const resolvers = handler.getDefinitions()

// biome-ignore lint/suspicious/noExplicitAny: This is a mock
export async function invoke(functionKey: string, payload?: any) {
  // Always provide a mock context with accountId for all calls
  const context = { accountId: 'local-dev-account-id' }
  // Special case for getEnv: inject userSettings as in production
  if (functionKey === 'getEnv') {
    return {
      posthog: {
        api_host: 'https://us.i.posthog.com',
        api_key: ''
      },
      stripe: {
        perishableKey: ''
      },
      userSettings: {
        emailProvided: false,
        emailPromptDismissed: false
      }
    }
  }
  const fn = resolvers[functionKey]
  if (!fn) throw new Error(`No resolver for ${functionKey}`)
  return fn({ payload, context })
}

export const view = {
  submit: async (_payload?: unknown): Promise<void> => {},
  close: async (_payload?: unknown): Promise<void> => {},
  refresh: async (_payload?: unknown): Promise<void> => {},
  createHistory: async () => {
    type MockLocation = {
      pathname: string
      search: string
      hash: string
      state: string | undefined
      key: string
    }
    type MockAction = 'POP' | 'PUSH' | 'REPLACE'
    type ToType = string | { pathname: string; search?: string; hash?: string }
    let listeners: Array<(location: MockLocation, action: MockAction) => void> =
      []
    let currentLocation: MockLocation = {
      pathname: String(window.location.pathname),
      search: String(window.location.search),
      hash: String(window.location.hash),
      state: undefined,
      key: 'default'
    }
    let action: MockAction = 'POP'

    function toPath(to: ToType): string {
      if (typeof to === 'string') return to
      if (typeof to === 'object' && to !== null) {
        const { pathname = '', search = '', hash = '' } = to
        return `${pathname}${search || ''}${hash || ''}`
      }
      return '/'
    }

    function notify(newLocation: MockLocation, newAction: MockAction) {
      currentLocation = newLocation
      action = newAction
      for (const fn of listeners) {
        fn(currentLocation, action)
      }
    }

    // Listen to popstate events to handle browser navigation
    window.addEventListener('popstate', () => {
      const newLocation: MockLocation = {
        ...currentLocation,
        pathname: String(window.location.pathname),
        search: String(window.location.search),
        hash: String(window.location.hash)
      }
      notify(newLocation, 'POP')
    })

    return {
      get action() {
        return action
      },
      get location() {
        return currentLocation
      },
      createHref: (to: ToType) => toPath(to),
      push: (to: ToType) => {
        const path = toPath(to)
        window.history.pushState({}, '', path)
        const newLocation: MockLocation = {
          ...currentLocation,
          pathname: String(window.location.pathname),
          search: String(window.location.search),
          hash: String(window.location.hash)
        }
        notify(newLocation, 'PUSH')
      },
      replace: (to: ToType) => {
        const path = toPath(to)
        window.history.replaceState({}, '', path)
        const newLocation: MockLocation = {
          ...currentLocation,
          pathname: String(window.location.pathname),
          search: String(window.location.search),
          hash: String(window.location.hash)
        }
        notify(newLocation, 'REPLACE')
      },
      go: (n: number) => {
        window.history.go(n)
      },
      back: () => {
        window.history.back()
      },
      forward: () => {
        window.history.forward()
      },
      listen: (fn: (location: MockLocation, action: MockAction) => void) => {
        listeners.push(fn)
        return () => {
          listeners = listeners.filter(l => l !== fn)
        }
      },
      block: () => () => {}
    }
  },
  getContext: async () => ({
    accountId: 'local-dev-account-id',
    cloudId: 'local-dev-cloud-id',
    workspaceId: 'local-dev-workspace-id',
    extension: {
      type: 'custom',
      url: 'http://localhost:3000',
      renderingLocation: 'compass'
    },
    environmentId: 'local-dev-env',
    environmentType: 'DEVELOPMENT',
    license: {
      active: true,
      billingPeriod: 'month',
      ccpEntitlementId: 'local-dev-entitlement-id',
      ccpEntitlementSlug: 'local-dev-entitlement-slug',
      isEvaluation: false,
      subscriptionEndDate: '2025-12-31',
      supportEntitlementNumber: 'local-dev-support-number',
      trialEndDate: null as string | null,
      type: 'paid'
    },
    localId: 'local-dev',
    locale: 'en-US',
    moduleKey: 'techradarglobal',
    siteUrl: 'http://localhost:3000',
    timezone: 'UTC',
    theme: null as string | null,
    surfaceColor: null as string | null
  }),
  theme: {
    enable: async (): Promise<void> => {}
  },
  changeWindowTitle: async (_title: string): Promise<void> => {}
}

export const router = {
  navigate: (path: string) => {
    window.location.href = path
  }
}
